"use client";

import Link from "next/link";
import type { Event, HomeContent, Testimonial } from "@/lib/api-types";
import { selectPublicHomeTestimonials } from "@/lib/testimonials-map";
import {
  HomeEventsEmptyState,
} from "@/components/home/cards/HomeEventCard";
import { HomeEventsSlideshow } from "@/components/home/HomeEventsSlideshow";
import { HomeTestimonialsRotator } from "@/components/home/HomeTestimonialsRotator";
import { HomeSectionHeader } from "@/components/home/HomeSectionHeader";
import { DEFAULT_HOME_CONTENT } from "@/lib/site-content-fallbacks";

interface HomeEventsTestimonialsProps {
  events: Event[];
  testimonials: Testimonial[];
  homeContent?: HomeContent;
}

/** Max upcoming events cycled in the home slideshow. */
const HOME_UPCOMING_CAP = 8;

export function HomeEventsTestimonials({
  events,
  testimonials,
  homeContent,
}: HomeEventsTestimonialsProps) {
  const upcoming = events
    .filter((e) => e.status.toLowerCase() === "upcoming")
    .sort((a, b) => Number(Boolean(b.featured)) - Number(Boolean(a.featured)))
    .slice(0, HOME_UPCOMING_CAP);

  const eventsSection =
    homeContent?.eventsSection ?? DEFAULT_HOME_CONTENT.eventsSection;
  const testimonialsSection =
    homeContent?.testimonialsSection ??
    DEFAULT_HOME_CONTENT.testimonialsSection;

  return (
    <section className="home-charity-section bg-[var(--charity-surface-alt)] py-16 md:py-20">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-2 lg:gap-12 md:px-6">
        <div>
          <HomeSectionHeader
            align="left"
            eyebrow={eventsSection.eyebrow}
            title={eventsSection.title}
            subtitle={eventsSection.subtitle}
            className="mb-6"
          />
          {upcoming.length === 0 ? (
            <HomeEventsEmptyState />
          ) : (
            <HomeEventsSlideshow events={upcoming} />
          )}
          <Link
            href="/events"
            className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary transition-colors hover:text-accent"
          >
            View all events →
          </Link>
        </div>

        <div>
          <HomeSectionHeader
            align="left"
            eyebrow={testimonialsSection.eyebrow}
            title={testimonialsSection.title}
            subtitle={testimonialsSection.subtitle}
            className="mb-6"
          />
          {testimonials.length === 0 ? (
            <p className="text-sm text-muted">No testimonials published yet.</p>
          ) : (
            <HomeTestimonialsRotator
              testimonials={selectPublicHomeTestimonials(testimonials)}
            />
          )}
        </div>
      </div>
    </section>
  );
}
